/**
 * Server administration hooks (spec §10.8, M26). Administrators only; the server answers 403 to
 * anyone else, and the page never asks unless `me.isAdmin`.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, unwrap } from "../api/client";
import type { components } from "../api/schema";

export type ServerUser = components["schemas"]["ServerUserOut"];
export type LlmTestResult = components["schemas"]["LlmTestOut"];
export type AdminEvent = components["schemas"]["EventOut"];
export type HistoryPage = components["schemas"]["HistoryOut"];

export type SettingKind = "flag" | "int" | "float" | "text" | "url" | "choice" | "secret";
export type SettingValue = boolean | number | string;

export interface SettingRow {
  key: string;
  type: SettingKind;
  value: SettingValue;
  base: SettingValue;
  source: "saved" | "environment" | "default";
  hidden: boolean;
  min: number | null;
  max: number | null;
  unit: "s" | "bytes" | "p" | null;
  choices: string[];
  optional: boolean;
  unreadable: boolean;
}

export interface ServerSettings {
  groups: { id: string; settings: SettingRow[] }[];
  environment: { key: string; value: string }[];
}

/** A field error from the server: `code` names it, the rest are its parameters. */
export type FieldError = { code: string } & Record<string, unknown>;

/** 422 `{errors}` or 409 `{code}`, kept whole so the page can translate them. */
export class ServerRefusal extends Error {
  constructor(
    public status: number,
    public errors: Record<string, FieldError>,
    public code: string | null,
  ) {
    super(code ?? Object.keys(errors).join(", "));
  }
}

async function refusing<T>(res: { data?: unknown; error?: unknown; response: Response }): Promise<T> {
  if (res.response.status === 422 || res.response.status === 409) {
    const body = (res.error ?? {}) as { errors?: Record<string, FieldError>; code?: string };
    throw new ServerRefusal(res.response.status, body.errors ?? {}, body.code ?? null);
  }
  return unwrap<T>(res);
}

const qk = {
  settings: ["server", "settings"] as const,
  users: ["server", "users"] as const,
  history: ["server", "history"] as const,
};

export function useServerSettings() {
  return useQuery({
    queryKey: qk.settings,
    queryFn: async () => unwrap<ServerSettings>(await api.GET("/api/server/settings")),
    staleTime: 0,
  });
}

export function useSaveServerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { values?: Record<string, SettingValue>; reset?: string[] }) =>
      refusing<ServerSettings>(await api.PATCH("/api/server/settings", { body: { values: body.values ?? {}, reset: body.reset ?? [] } })),
    onSuccess: (data) => {
      qc.setQueryData(qk.settings, data);
      void qc.invalidateQueries({ queryKey: qk.history });
    },
  });
}

export function useLlmTest() {
  return useMutation({
    mutationFn: async (body: { target: "llm" | "decision"; baseUrl?: string; model?: string; apiKey?: string }) =>
      unwrap<LlmTestResult>(await api.POST("/api/server/llm-test", { body })),
  });
}

export function useServerUsers() {
  return useQuery({ queryKey: qk.users, queryFn: async () => unwrap<ServerUser[]>(await api.GET("/api/server/users")) });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UserIn"]) => refusing<ServerUser>(await api.POST("/api/server/users", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      void qc.invalidateQueries({ queryKey: qk.history });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & components["schemas"]["UserPatch"]) =>
      refusing<ServerUser>(await api.PATCH("/api/server/users/{user_id}", { params: { path: { user_id: id } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      void qc.invalidateQueries({ queryKey: qk.history });
    },
  });
}

/** [M28] Revoke every MCP token of a person (spec §8.27). */
export function useRevokeUserTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap<{ revoked: number }>(await api.DELETE("/api/server/users/{user_id}/tokens", { params: { path: { user_id: id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      void qc.invalidateQueries({ queryKey: qk.history });
    },
  });
}

export function useServerHistory() {
  return useInfiniteQuery({
    queryKey: qk.history,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      unwrap<HistoryPage>(await api.GET("/api/server/history", { params: { query: { before: pageParam ?? undefined, limit: 50 } } })),
    getNextPageParam: (last) => last.next,
  });
}

export { ApiError };
